import { voiceService } from '../services/voice-service';
import { sendMessage } from './ai-helper';
import { isPodcastMode, isRecording, handlePodcastUserInput } from './avi-podcast';

declare function showToast(msg: string): void;

let aviConversationId: string | null = null;
let isAviActive = false;
(window as any).isAviActive = () => isAviActive;
let mouthTimer: any = null;
let aviHistory: { role: string; content: string }[] = [];

export function initAvi() {
    console.log('initAvi - Initializing Artificial Virtual Intelligence');
    const toggle = document.getElementById('aviToggle');
    if (toggle) {
        toggle.classList.remove('active');
        isAviActive = false;
        updateAviCaptions('System Standby. Click power to initialize.');
    }

    // Global Event Listeners for VoiceService synchronization
    window.addEventListener('voice-interim-result', (e: any) => {
        if (isAviActive) {
            updateAviCaptions(e.detail.text);
            setEmotion('thinking');
        }
    });

    window.addEventListener('voice-speak-start', () => {
        if (isAviActive) {
            startMouthSync();
            setEmotion('happy');
            showToast('AVI: Speaking response');
        }
    });

    window.addEventListener('voice-speak-end', () => {
        if (isAviActive) {
            stopMouthSync();
            setEmotion('neutral');
            updateAviCaptions('Listening...');
        }
    });

    window.addEventListener('avi-interrupted', () => {
        if (isAviActive) {
            stopMouthSync();
            setEmotion('thinking');
            updateAviCaptions('Interrupting...');
            showToast('AVI: Speech Interrupted!');
        }
    });
}

(window as any).toggleAviPower = () => {
    isAviActive = !isAviActive;
    const toggle = document.getElementById('aviToggle');
    const face = document.getElementById('aviFace');
    const waveform = document.getElementById('aviWaveform');

    if (isAviActive) {
        toggle?.classList.add('active');
        waveform?.classList.add('active');
        updateAviCaptions('A.V.I. Online. Listening...');
        setEmotion('happy');
        showToast('AVI Power: ON');
        if (typeof (window as any).setVoiceActive === 'function') {
            (window as any).setVoiceActive(true);
        }
        _startAviListening();
    } else {
        toggle?.classList.remove('active');
        waveform?.classList.remove('active');
        voiceService.stopListening();
        voiceService.cancelSpeech();
        updateAviCaptions('Standby.');
        setEmotion('neutral');
        stopMouthSync();
        showToast('AVI Power: OFF');
        if (typeof (window as any).setVoiceActive === 'function') {
            (window as any).setVoiceActive(false);
        }
    }
};

function _startAviListening() {
    if (!isAviActive) return;
    showToast('AVI: Started Listening...');

    voiceService.startListening((text: string) => {
        if (!text || !isAviActive) return;

        updateAviCaptions(text);
        setEmotion('thinking');
        showToast('AVI: Silence Detected, Auto-Sending');
        
        // Auto-send result from silence timer
        _sendToAvi(text);
    });
}

async function _sendToAvi(text: string) {
    if (!text.trim()) return;
    
    // Check if in podcast mode
    if (isPodcastMode && isRecording) {
        handlePodcastUserInput(text);
        return;
    }
    
    setEmotion('thinking');
    updateAviCaptions('Processing...');

    try {
        const { fetchAiToolsMetaModel } = await import('./ai-helper');
        const systemPrompt = "You are A.V.I. (Artificial Virtual Intelligence), a voice-first AI companion built by itsKayWat. Respond conversationally and concisely. Avoid markdown formatting since your response will be spoken aloud.";
        const context = [{ role: 'system', content: systemPrompt }];
        
        // Push any prior AVI conversation history
        if (aviHistory.length > 0) {
            context.push(...aviHistory);
        }
        context.push({ role: 'user', content: text });

        const response = await fetchAiToolsMetaModel(text, context);
        
        // Store in local AVI history for context continuity
        aviHistory.push({ role: 'user', content: text });
        aviHistory.push({ role: 'assistant', content: response });
        if (aviHistory.length > 20) aviHistory.splice(0, 2); // trim old history

        updateAviCaptions(response);
        setEmotion('happy');
        
        // Fetch current settings and speak
        const settings = getAviSettings();
        voiceService.speak(response, {
            voiceName: settings.voiceName,
            rate: settings.rate,
            pitch: settings.pitch,
            volume: settings.volume
        });

    } catch (err: any) {
        console.error('[AVI] Error getting response:', err);
        updateAviCaptions('Sorry, I encountered an error. Please try again.');
        setEmotion('neutral');
    }
}

export function updateAviCaptions(text: string) {
    const caps = document.getElementById('aviCaptions');
    if (caps) caps.innerText = text;
}

export function setEmotion(emotion: 'happy' | 'sad' | 'thinking' | 'neutral') {
    const face = document.getElementById('aviFace');
    if (!face) return;
    
    face.classList.remove('happy', 'sad', 'thinking');
    if (emotion !== 'neutral') face.classList.add(emotion);
    
    const eyes = face.querySelectorAll('.avi-eye');
    eyes.forEach(eye => {
        eye.classList.remove('narrow', 'large');
        if (emotion === 'happy') eye.classList.add('large');
        if (emotion === 'thinking') eye.classList.add('narrow');
    });
}

export function startMouthSync() {
    const mouth = document.getElementById('aviMouth');
    if (!mouth) return;

    if (mouthTimer) clearInterval(mouthTimer);
    
    mouthTimer = setInterval(() => {
        const jitter = 5 + Math.random() * 45;
        const widthJitter = 60 + Math.random() * 40;
        mouth.style.height = `${jitter}px`;
        mouth.style.width = `${widthJitter}px`;
        mouth.style.borderRadius = jitter > 25 ? '50%' : '20px';
    }, 80).toString();
}

export function stopMouthSync() {
    const mouth = document.getElementById('aviMouth');
    if (mouth) {
        mouth.style.height = '4px';
        mouth.style.width = '60px';
        mouth.style.borderRadius = '20px';
    }
    if (mouthTimer) {
        clearInterval(parseInt(mouthTimer));
        mouthTimer = null;
    }
}

// Global hooks for setup
(window as any).initAvi = initAvi;

// ── AVI Settings Bridge ───────────────────────────────────────────────
// Called by saveAviSettings() in app.ts to apply new settings live
(window as any).applyAviSettingsLive = (settings: any) => {
    // Update AVI history limit
    if (settings.historyLength !== undefined) {
        const maxPairs = settings.historyLength * 2;
        if (aviHistory.length > maxPairs) {
            aviHistory.splice(0, aviHistory.length - maxPairs);
        }
    }
    // Update silence threshold on the voice service
    if (settings.silenceThreshold !== undefined && typeof voiceService.setSilenceThreshold === 'function') {
        voiceService.setSilenceThreshold(settings.silenceThreshold);
    }
};

// Helper for avi.ts to read current settings from localStorage
export function getAviSettings() {
    const raw = localStorage.getItem('avi_settings');
    return raw ? JSON.parse(raw) : {
        voiceName: '', rate: 1.05, pitch: 1.0, volume: 1.0,
        silenceThreshold: 1500, historyLength: 10, autoListen: true
    };
}
